import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import logo_dark from '../../assets/images/logo-dark.png';
import logo_light from '../../assets/images/logo-light.png';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Turnstile from '../../components/Turnstile';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;
const inputClass = "form-input w-full text-[15px] py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded-full outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0 mt-3";

export default function Signup() {
    const [step, setStep] = useState('details'); // 'details' | 'code'
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [acceptTnC, setAcceptTnC] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [captchaToken, setCaptchaToken] = useState('');

    const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
    const [cooldown, setCooldown] = useState(0);
    const digitRefs = useRef([]);
    const captchaRef = useRef(null);
    const navigate = useNavigate();

    // Обратный отсчёт до следующей возможной отправки кода
    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    useEffect(() => {
        if (step === 'code' && digitRefs.current[0]) {
            digitRefs.current[0].focus();
        }
    }, [step]);

    const readError = (err, fallback) =>
        err?.response?.data?.message || fallback;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!acceptTnC) {
            setError('Please accept the Terms and Conditions');
            return;
        }
        if (password !== repeatPassword) {
            setError('Passwords do not match');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }
        if (!captchaToken) {
            setError('Please complete the captcha');
            return;
        }

        setSubmitting(true);
        try {
            const response = await axios.post('https://nifty-art.com/api/auth/signup', {
                firstName,
                lastName,
                username: `@${username}`,
                email,
                password,
                captchaToken,
            });

            if (response.data.success) {
                setStep('code');
                setDigits(Array(CODE_LENGTH).fill(''));
                setCooldown(RESEND_COOLDOWN_SECONDS);
                setSuccess(response.data.message || `We sent a ${CODE_LENGTH}-digit code to ${email}.`);
            } else {
                setError(response.data.message || 'Failed to register');
                captchaRef.current?.reset();
            }
        } catch (err) {
            const retryAfter = err?.response?.data?.retryAfterSeconds;
            if (retryAfter) setCooldown(retryAfter);
            setError(readError(err, 'Something went wrong, please try again.'));
            // Токен Turnstile одноразовый — после неудачи нужен новый
            captchaRef.current?.reset();
        } finally {
            setSubmitting(false);
        }
    };

    const submitCode = useCallback(async (code) => {
        setError('');
        setSuccess('');
        setSubmitting(true);
        try {
            const response = await axios.post('https://nifty-art.com/api/auth/verify-code', {
                email,
                code,
            });

            if (response.data.success) {
                navigate('/login', {
                    state: { message: 'Email verified. You can sign in now.' },
                });
            } else {
                setError(response.data.message || 'Invalid code');
            }
        } catch (err) {
            setDigits(Array(CODE_LENGTH).fill(''));
            digitRefs.current[0]?.focus();
            setError(readError(err, 'Could not verify the code. Please try again.'));
        } finally {
            setSubmitting(false);
        }
    }, [email, navigate]);

    const handleDigitChange = (index, rawValue) => {
        const value = rawValue.replace(/\D/g, '');
        if (!value) {
            setDigits((prev) => prev.map((d, i) => (i === index ? '' : d)));
            return;
        }

        // Вставка целого кода из буфера обмена распределяется по всем полям
        const next = [...digits];
        value.split('').forEach((char, offset) => {
            if (index + offset < CODE_LENGTH) next[index + offset] = char;
        });
        setDigits(next);

        const filledTo = Math.min(index + value.length, CODE_LENGTH - 1);
        digitRefs.current[filledTo]?.focus();

        // Код набран полностью — отправляем не дожидаясь нажатия кнопки
        if (next.every((d) => d !== '')) {
            submitCode(next.join(''));
        }
    };

    const handleDigitKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !digits[index] && index > 0) {
            digitRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowLeft' && index > 0) {
            digitRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
            digitRefs.current[index + 1]?.focus();
        }
    };

    const handleVerifySubmit = (e) => {
        e.preventDefault();
        const code = digits.join('');
        if (code.length < CODE_LENGTH) {
            setError(`Enter all ${CODE_LENGTH} digits.`);
            return;
        }
        submitCode(code);
    };

    const handleResend = async () => {
        if (cooldown > 0 || submitting) return;
        setError('');
        setSuccess('');
        setSubmitting(true);
        try {
            const response = await axios.post('https://nifty-art.com/api/auth/resend-code', { email });
            setCooldown(RESEND_COOLDOWN_SECONDS);
            setDigits(Array(CODE_LENGTH).fill(''));
            digitRefs.current[0]?.focus();
            setSuccess(response.data.message || 'A new code has been sent.');
        } catch (err) {
            const retryAfter = err?.response?.data?.retryAfterSeconds;
            if (retryAfter) setCooldown(retryAfter);
            setError(readError(err, 'Could not resend the code. Please try again.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <section className="min-h-screen py-20 md:py-36 flex items-center overflow-y-auto bg-[url('../../assets/images/cta.jpg')] bg-no-repeat bg-center">
                <div className="absolute top-0 start-0 w-full h-full z-0 pointer-events-none overflow-hidden">
                    <iframe title="iframe" src="https://player.vimeo.com/video/502163294?background=1&autoplay=1&loop=1&byline=0&title=0" className="absolute top-1/2 start-1/2 ltr:-translate-x-1/2 rtl:translate-x-1/2 -translate-y-1/2 w-screen h-[56.25vw] min-h-screen min-w-[177.77vw]"></iframe>
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black"></div>
                <div className="container">
                    <div className="flex justify-center">
                        <div className="max-w-[400px] w-full m-auto p-6 bg-white dark:bg-slate-900 shadow-md dark:shadow-gray-800 rounded-md">
                            <Link to="/">
                                <img src={logo_dark} className="mx-auto h-7 block dark:hidden" alt="" />
                                <img src={logo_light} className="mx-auto h-7 dark:block hidden" alt="" />
                            </Link>

                            {step === 'details' ? (
                                <>
                                    <h5 className="my-6 text-xl font-semibold">Signup</h5>
                                    <form className="text-start" onSubmit={handleSubmit}>
                                        <div className="grid grid-cols-1">
                                            {error && <p className="text-red-500 mb-4">{error}</p>}
                                            {success && <p className="text-green-500 mb-4">{success}</p>}
                                            <div className="mb-4">
                                                <label className="font-semibold" htmlFor="firstName">First Name:</label>
                                                <input
                                                    id="firstName"
                                                    type="text"
                                                    value={firstName}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    className={inputClass}
                                                    placeholder="Harry"
                                                />
                                            </div>
                                            <div className="mb-4">
                                                <label className="font-semibold" htmlFor="lastName">Last Name:</label>
                                                <input
                                                    id="lastName"
                                                    type="text"
                                                    value={lastName}
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    className={inputClass}
                                                    placeholder="Potter"
                                                />
                                            </div>
                                            <div className="mb-4">
                                                <label className="font-semibold" htmlFor="username">Username:</label>
                                                <input
                                                    id="username"
                                                    type="text"
                                                    value={username}
                                                    onChange={(e) => setUsername(e.target.value)}
                                                    className={inputClass}
                                                    placeholder="Username"
                                                />
                                            </div>
                                            <div className="mb-4">
                                                <label className="font-semibold" htmlFor="LoginEmail">Email Address:</label>
                                                <input
                                                    id="LoginEmail"
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    className={inputClass}
                                                    placeholder="name@example.com"
                                                    required
                                                />
                                                <p className="text-slate-400 text-sm mt-2">
                                                    We will send a {CODE_LENGTH}-digit confirmation code to this address.
                                                </p>
                                            </div>

                                            <div className="mb-4">
                                                <label className="font-semibold" htmlFor="LoginPassword">Password:</label>
                                                <input
                                                    id="LoginPassword"
                                                    type="password"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    className={inputClass}
                                                    placeholder="Password:"
                                                />
                                            </div>
                                            <div className="mb-4">
                                                <label className="font-semibold" htmlFor="repeatPassword">Repeat Password:</label>
                                                <input
                                                    id="repeatPassword"
                                                    type="password"
                                                    value={repeatPassword}
                                                    onChange={(e) => setRepeatPassword(e.target.value)}
                                                    className={inputClass}
                                                    placeholder="Repeat Password"
                                                />
                                            </div>
                                            <div className="flex items-center mb-4">
                                                <input
                                                    className="form-checkbox rounded border-gray-200 dark:border-gray-800 text-violet-600 focus:border-violet-600/30 focus:ring focus:ring-offset-0 focus:ring-violet-600/20 focus:ring-opacity-50 me-2"
                                                    type="checkbox"
                                                    id="AcceptT&C"
                                                    checked={acceptTnC}
                                                    onChange={(e) => setAcceptTnC(e.target.checked)}
                                                />
                                                <label className="form-checkbox-label text-slate-400" htmlFor="AcceptT&C">I Accept <Link to="/terms" className="text-violet-600">Terms And Condition</Link></label>
                                            </div>

                                            <Turnstile ref={captchaRef} onVerify={setCaptchaToken} />

                                            <div className="mb-4">
                                                <input
                                                    type="submit"
                                                    className="btn bg-violet-600 hover:bg-violet-700 border-violet-600 hover:border-violet-700 text-white rounded-full w-full disabled:opacity-60"
                                                    value={submitting ? 'Sending code...' : 'Register'}
                                                    disabled={submitting}
                                                />
                                            </div>

                                            <div className="text-center">
                                                <span className="text-slate-400 me-2">Already have an account ? </span> <Link to="/login" className="text-black dark:text-white font-bold">Sign in</Link>
                                            </div>
                                        </div>
                                    </form>
                                </>
                            ) : (
                                <>
                                    <h5 className="mt-6 mb-2 text-xl font-semibold">Confirm your email</h5>
                                    <p className="text-slate-400 text-sm mb-6">
                                        Enter the {CODE_LENGTH}-digit code we sent to <span className="font-semibold text-black dark:text-white break-all">{email}</span>
                                    </p>

                                    <form className="text-start" onSubmit={handleVerifySubmit}>
                                        {error && <p className="text-red-500 mb-4">{error}</p>}
                                        {success && <p className="text-green-500 mb-4">{success}</p>}

                                        <div className="flex justify-between gap-2 mb-6" dir="ltr">
                                            {digits.map((digit, index) => (
                                                <input
                                                    key={index}
                                                    ref={(el) => { digitRefs.current[index] = el; }}
                                                    type="text"
                                                    inputMode="numeric"
                                                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                                                    maxLength={CODE_LENGTH}
                                                    value={digit}
                                                    onChange={(e) => handleDigitChange(index, e.target.value)}
                                                    onKeyDown={(e) => handleDigitKeyDown(index, e)}
                                                    onFocus={(e) => e.target.select()}
                                                    aria-label={`Digit ${index + 1}`}
                                                    className="form-input w-full h-12 text-center text-xl font-semibold bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded-md outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0 px-0"
                                                />
                                            ))}
                                        </div>

                                        <div className="mb-4">
                                            <button
                                                type="submit"
                                                className="btn bg-violet-600 hover:bg-violet-700 border-violet-600 hover:border-violet-700 text-white rounded-full w-full disabled:opacity-60"
                                                disabled={submitting}
                                            >
                                                {submitting ? 'Verifying...' : 'Verify email'}
                                            </button>
                                        </div>

                                        <div className="text-center text-sm">
                                            <span className="text-slate-400 me-2">Didn't get the code?</span>
                                            <button
                                                type="button"
                                                onClick={handleResend}
                                                disabled={cooldown > 0 || submitting}
                                                className="text-violet-600 font-semibold disabled:text-slate-400 disabled:cursor-not-allowed"
                                            >
                                                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                                            </button>
                                        </div>

                                        <div className="text-center mt-4">
                                            <button
                                                type="button"
                                                onClick={() => { setStep('details'); setError(''); setSuccess(''); }}
                                                className="text-slate-400 text-sm hover:text-violet-600"
                                            >
                                                Use a different email
                                            </button>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
